'use client';

import React from 'react';

export interface FilterCheckboxOption {
  /** Unique id — used as React key and checkbox name. */
  slug: string;
  /** Visible label text. */
  title: string;
}

interface FilterCheckboxItemProps {
  /** Option data (slug for key/name, title for the visible label). */
  option: FilterCheckboxOption;
  /** Whether the option is currently selected. */
  checked: boolean;
  /** Toggle handler — called with the option slug. */
  onToggle: (slug: string) => void;
}

function CheckIcon() {
  return (
    <svg
      className="h-3.5 w-3.5 text-white"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="4"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

/**
 * Atomic custom checkbox row for filter lists.
 * A native (visually hidden) input drives behaviour and a11y;
 * the styled box + label text reflect the checked state.
 */
const FilterCheckboxItem: React.FC<FilterCheckboxItemProps> = ({
  option,
  checked,
  onToggle,
}) => (
  <label
    key={option.slug}
    className="group flex cursor-pointer items-center py-2 select-none"
  >
    <input
      type="checkbox"
      name={option.slug}
      checked={checked}
      onChange={() => onToggle(option.slug)}
      className="peer hidden"
    />
    <div
      className={`flex h-5 w-5 items-center justify-center rounded border transition-all ${
        checked
          ? 'bg-theme-action-primary border-theme-action-primary'
          : 'border-gray-300 bg-white'
      }`}
    >
      {checked && <CheckIcon />}
    </div>
    <span
      className={`text-body-lg ml-3 ${
        checked
          ? 'text-theme-action-primary font-medium'
          : 'text-theme-dark-deep'
      }`}
    >
      {option.title}
    </span>
  </label>
);

export default FilterCheckboxItem;
